import React, { useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import BarcodeIcon from "@mui/icons-material/QrCodeScanner";
import type { Product } from "../types/pos";
import api from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";

// For POS: Product type should at least include _id, name, price, sku

type Props = {
  onSelect: (p: Product) => void;
};

const ProductSearch: React.FC<Props> = ({ onSelect }) => {
  const { token } = useAuth();
  const [query, setQuery] = useState("");
  const [barcode, setBarcode] = useState("");
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newProduct, setNewProduct] = useState<{ name: string; price: number; sku?: string }>({
    name: "",
    price: 0,
    sku: "",
  });

  // Fetch inventory items from backend as user types (debounce!)
  useEffect(() => {
    let isActive = true;
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await api.get("/inventory", {
          params: barcode
            ? { search: barcode }
            : query
              ? { search: query }
              : {},
          headers: { Authorization: `Bearer ${token}` },
        });
        if (isActive) setProducts(res.data || []);
      } catch {
        if (isActive) setProducts([]);
      } finally {
        if (isActive) setLoading(false);
      }
    };
    // Only search if query/barcode present, or show nothing
    if (query.length > 0 || barcode.length > 0) fetch();
    else setProducts([]);
    return () => { isActive = false; };
  }, [query, barcode, token]);

  // Handle barcode input (simulate scanner or type)
  const handleBarcodeScan = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBarcode(e.target.value);
    setQuery(""); // clear query so barcode takes over
  };

  // Add a new product (should POST to backend, but here just a placeholder)
  const handleAddProduct = async () => {
    if (!newProduct.name || !newProduct.price) return;
    try {
      // Ideally POST to /inventory (with proper structure)
      // const res = await api.post("/inventory", newProduct, { headers: { Authorization: `Bearer ${token}` } });
      // setProducts(prev => [res.data, ...prev]);
      // For now, add locally:
      const added: Product = {
        _id: Math.random().toString(36).substring(2),
        name: newProduct.name,
        price: newProduct.price,
        sku: newProduct.sku,
      };
      setProducts((prev) => [added, ...prev]);
      setShowAddDialog(false);
      setNewProduct({ name: "", price: 0, sku: "" });
    } catch {
      alert("Failed to add product (not implemented)");
    }
  };

  return (
    <div className="mb-3">
      <div className="flex flex-col sm:flex-row items-end gap-2 mb-2">
        {/* Search by name/SKU */}
        <TextField
          label="Search by Name or SKU"
          size="small"
          variant="outlined"
          value={query}
          onChange={e => {
            setQuery(e.target.value);
            setBarcode(""); // typing query disables barcode
          }}
          className="flex-1"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon color="primary" />
              </InputAdornment>
            ),
          }}
        />
        {/* Barcode input */}
        <TextField
          label="Scan Barcode"
          size="small"
          variant="outlined"
          value={barcode}
          onChange={handleBarcodeScan}
          className="w-40"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <BarcodeIcon color="primary" />
              </InputAdornment>
            ),
          }}
        />
        {/* Add product button */}
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          color="primary"
          onClick={() => setShowAddDialog(true)}
        >
          Add New Product
        </Button>
      </div>
      {/* Results */}
      <div className="bg-white rounded shadow p-2 mt-2 max-h-44 overflow-auto border border-gray-100">
        {loading && <div className="text-blue-600 text-center py-2">Loading...</div>}
        {!loading && products.length === 0 && (
          <div className="text-gray-400 text-center py-2">No products found</div>
        )}
        {products.map((p) => (
          <div
            key={p._id}
            className="flex justify-between items-center hover:bg-blue-100 transition px-3 py-2 rounded cursor-pointer"
            tabIndex={0}
            onClick={() => onSelect(p)}
            onKeyPress={e => { if (e.key === "Enter") onSelect(p); }}
          >
            <span>
              <span className="font-medium text-gray-700">{p.name}</span>
              {p.sku && (
                <span className="ml-2 text-xs text-blue-600 bg-blue-50 px-2 rounded">{p.sku}</span>
              )}
            </span>
            <span className="font-bold text-gray-800">
              ${typeof p.price === "number" ? p.price.toFixed(2) : "--"}
            </span>
          </div>
        ))}
      </div>
      {/* Add Product Dialog */}
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add New Product</DialogTitle>
        <DialogContent>
          <TextField
            label="Product Name"
            value={newProduct.name}
            onChange={e => setNewProduct((np) => ({ ...np, name: e.target.value }))}
            fullWidth
            margin="dense"
            required
          />
          <TextField
            label="SKU (optional)"
            value={newProduct.sku}
            onChange={e => setNewProduct((np) => ({ ...np, sku: e.target.value }))}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Price"
            type="number"
            value={newProduct.price}
            onChange={e => setNewProduct((np) => ({ ...np, price: Number(e.target.value) }))}
            fullWidth
            margin="dense"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>Cancel</Button>
          <Button onClick={handleAddProduct} variant="contained" color="primary">
            Add Product
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ProductSearch;
